import { useController, useFormContext } from 'react-hook-form';
import { Select } from '@mantine/core';
import { SelectOptionWithIcon } from '@components/shared/SelectOptionWithIcon';
import { ReactNode } from 'react';

type OptionType = {
  value: string;
  label: string;
  subtitle?: string;
  icon?: ReactNode;
  type?: string; // for group
  [key: string]: unknown;
};

type SelectWithIconProps = {
  name: string;
  label?: string; // label is now optional
  placeholder: string;
  data: { group: string; items: OptionType[] }[];
  disabled?: boolean;
  required?: boolean;
  onChange?: (value: string | null) => void;
  w?: string | number; // <-- new prop for css "w" (width)
  style?: React.CSSProperties; // pass other inline styles
  [key: string]: any; // allow more Mantine props (e.g. maxWidth, className, etc.)
};

export const SelectWithIcon = ({
  name,
  label,
  placeholder,
  data,
  disabled,
  required,
  onChange,
  style,
  ...rest // <-- for any further props
}: SelectWithIconProps) => {
  const { control, setValue, watch } = useFormContext();
  const { field, fieldState } = useController({
    name,
    control,
  });

  const error = fieldState.error?.message;
  const currentValue = watch(name);

  return (
    <Select
      label={label}
      placeholder={placeholder}
      data={data}
      value={currentValue ?? null}
      clearable
      onChange={value => {
        setValue(name, value ?? '', {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
        field.onChange(value ?? '');
        if (onChange) onChange(value);
      }}
      onBlur={field.onBlur}
      disabled={disabled}
      searchable
      required={required}
      style={style}
      {...rest}
      error={error}
      renderOption={({ option }) => {
        // Render group headers
        if ((option as any).type === 'group') {
          return (
            <div
              style={{
                padding: '8px 0',
                fontWeight: 600,
                fontSize: '12px',
                color: '#868e96',
              }}
            >
              {option.label}
            </div>
          );
        }
        // Render options with icons
        return (
          <>
            <SelectOptionWithIcon
              option={option as any}
              selected={option.value === field.value}
            />
          </>
        );
      }}
    />
  );
};

export default SelectWithIcon;
